import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { getSchoolAssetUrl } from '../utils/api';
import {
  LayoutDashboard, Users, GraduationCap, BookOpen, Calendar,
  ClipboardList, IndianRupee, Library, FileText,
  School, Bell, Clock, ChevronDown
} from 'lucide-react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from './ui/collapsible';

const Sidebar = ({ isOpen, setIsOpen }) => {
  const { user, isAdmin } = useAuth();
  const location = useLocation();
  const [schoolDetails, setSchoolDetails] = useState(null);

  const isSuperAdmin = user?.role_slug === 'super_admin';
  const schoolId = user?.school_id;

  useEffect(() => {
    if (!schoolId || isSuperAdmin) {
      setSchoolDetails(null);
      return;
    }
    let cancelled = false;
    api.get(`schools/${schoolId}`)
      .then((res) => {
        if (!cancelled && res.data?.data) setSchoolDetails(res.data.data);
      })
      .catch(() => {
        if (!cancelled) setSchoolDetails(null);
      });
    return () => { cancelled = true; };
  }, [schoolId, isSuperAdmin]);

  const navGroups = [
    {
      key: 'academics',
      label: 'Academics',
      icon: BookOpen,
      items: [
        { path: '/classes', icon: BookOpen, label: 'Classes', adminOnly: true },
        { path: '/timetable', icon: Clock, label: 'Timetable' },
        { path: '/exams', icon: FileText, label: 'Exams' },
        { path: '/attendance', icon: ClipboardList, label: 'Attendance' },
        { path: '/events', icon: Calendar, label: 'Calendar' },
        { path: '/announcements', icon: Bell, label: 'Announcements' },
      ],
    },
    {
      key: 'admission',
      label: 'Admission',
      icon: GraduationCap,
      items: [
        { path: '/students', icon: GraduationCap, label: 'Students', adminOnly: true },
      ],
    },
    {
      key: 'study',
      label: 'Study Material',
      icon: Library,
      items: [
        { path: '/library', icon: Library, label: 'Library' },
      ],
    },
    {
      key: 'hr',
      label: 'HR',
      icon: Users,
      items: [
        { path: '/teachers', icon: Users, label: 'Teachers', adminOnly: true },
      ],
    },
    {
      key: 'fees',
      label: 'Fees',
      icon: IndianRupee,
      items: [
        { path: '/fees', icon: IndianRupee, label: 'Fees', adminOnly: true },
      ],
    },
  ];

  const [openGroups, setOpenGroups] = useState({
    academics: true,
    admission: true,
    study: true,
    hr: true,
    fees: true,
  });

  const filterItem = (item) => {
    if (item.superAdminOnly && user?.role_slug !== 'super_admin') return false;
    if (item.adminOnly && !isAdmin) return false;
    return true;
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 z-50 transform transition-transform duration-300 lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo / Branding: super admin = ERP branding; school user = school logo + name */}
          <div className="p-6 border-b border-slate-800">
            <div className="flex items-center gap-3">
              {isSuperAdmin ? (
                <>
                  <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shrink-0">
                    <GraduationCap className="w-6 h-6 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h1 className="text-lg font-bold text-white">School ERP</h1>
                    <p className="text-xs text-slate-500">Management System</p>
                  </div>
                </>
              ) : schoolDetails ? (
                <>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 overflow-hidden bg-slate-800">
                    {schoolDetails.logo ? (
                      <img
                        src={getSchoolAssetUrl(schoolDetails.logo, schoolId)}
                        alt={schoolDetails.name || 'School'}
                        className="w-10 h-10 object-contain"
                      />
                    ) : (
                      <BookOpen className="w-6 h-6 text-slate-400" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h1 className="text-lg font-bold text-white truncate" title={schoolDetails.name}>
                      {schoolDetails.name || 'School'}
                    </h1>
                    <p className="text-xs text-slate-500">Management System</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-10 h-10 bg-slate-700 rounded-xl flex items-center justify-center shrink-0">
                    <BookOpen className="w-6 h-6 text-slate-400" />
                  </div>
                  <div className="min-w-0">
                    <h1 className="text-lg font-bold text-white">School ERP</h1>
                    <p className="text-xs text-slate-500">Management System</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <ul className="space-y-1">
              <li>
                <NavLink
                  to="/dashboard"
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    `relative flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                    }`
                  }
                  data-testid="nav-dashboard"
                >
                  <LayoutDashboard className="w-5 h-5" />
                  <span className="font-medium">Dashboard</span>
                </NavLink>
              </li>

              {user?.role_slug === 'super_admin' && (
                <li>
                  <NavLink
                    to="/schools"
                    onClick={() => setIsOpen(false)}
                    className={({ isActive }) =>
                      `relative flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                      }`
                    }
                    data-testid="nav-schools"
                  >
                    <School className="w-5 h-5" />
                    <span className="font-medium">Schools</span>
                  </NavLink>
                </li>
              )}

              {navGroups.map((group) => {
                const groupItems = group.items.filter(filterItem);
                if (groupItems.length === 0) return null;
                const GroupIcon = group.icon;
                const isOpen = !!openGroups[group.key];
                const isGroupActive = groupItems.some(
                  (item) =>
                    location?.pathname === item.path ||
                    location?.pathname?.startsWith(`${item.path}/`)
                );
                return (
                  <li key={group.key} className="pt-2">
                    <Collapsible open={isOpen} onOpenChange={(v) => setOpenGroups({ ...openGroups, [group.key]: v })}>
                      <div className="relative">
                        <span
                          className={`absolute left-0 top-0 bottom-0 w-[2px] rounded-full bg-blue-500/70 transition-opacity ${
                            isGroupActive ? 'opacity-100' : 'opacity-0'
                          }`}
                        />
                      <CollapsibleTrigger asChild>
                        <button
                          type="button"
                          className={`relative w-[calc(100%-8px)] ml-2 flex items-center gap-2 pl-1 pr-3 py-2 rounded-lg transition-colors ${
                            isGroupActive ? 'text-white bg-slate-800/60' : 'text-slate-300 hover:text-white hover:bg-slate-800'
                          }`}
                        >
                          <GroupIcon className="w-4 h-4 text-slate-400" />
                          <span className="text-sm font-semibold tracking-wide">{group.label}</span>
                          <ChevronDown className={`ml-auto w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-1">
                        <div className="pl-3 space-y-1 border-l border-slate-800/70">
                          {groupItems.map((item) => {
                            const ItemIcon = item.icon;
                            return (
                              <NavLink
                                key={item.path}
                                to={item.path}
                                onClick={() => setIsOpen(false)}
                                className={({ isActive }) =>
                                  `group relative flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                                    isActive
                                    ? 'text-white bg-blue-600'
                                      : 'text-slate-400 hover:text-white hover:bg-slate-800/70'
                                  }`
                                }
                                data-testid={`nav-${item.path.replace('/', '')}`}
                              >
                                <ItemIcon className="w-4 h-4" />
                                <span>{item.label}</span>
                              </NavLink>
                            );
                          })}
                        </div>
                      </CollapsibleContent>
                      </div>
                    </Collapsible>
                  </li>
                );
              })}
            </ul>
          </nav>

        </div>
      </aside>
    </>
  );
};

export default Sidebar;
